import { useCallback, useEffect, useState } from 'react'
import { useLingui } from '@lingui/react/macro'
import { useAppStore } from '@/lib/app-store'
import { OPEN_PREFERENCES_EVENT, readConsent, writeConsent, type ConsentCategories } from '@/lib/cookie-consent'
import { Dialog, DialogContent } from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Switch } from '@/components/ui/switch'

export function CookieBanner() {
  const { t } = useLingui()
  const sdk = useAppStore((state) => state.store.sdk)

  // `decided` mirrors whether the visitor has made a choice (banner hidden once true). Seeded from
  // the cookie on mount — safe because the banner only renders client-side (wrapped in ClientOnly).
  const [decided, setDecided] = useState(() => readConsent().decided)
  const [dialogOpen, setDialogOpen] = useState(false)
  // Draft toggle state for the preferences dialog. Committed only on an explicit save/accept/reject,
  // so flipping a switch no longer writes a cookie immediately (clearer, reversible UX).
  const [draft, setDraft] = useState<ConsentCategories>({ statistics: false, marketing: false })

  // Allow the dialog to be reopened from anywhere (e.g. the footer "Cookie settings" link) so users
  // can change or withdraw consent as easily as they gave it.
  useEffect(() => {
    const open = () => {
      const current = readConsent()
      setDraft({ statistics: current.statistics, marketing: current.marketing })
      setDialogOpen(true)
    }
    window.addEventListener(OPEN_PREFERENCES_EVENT, open)
    return () => window.removeEventListener(OPEN_PREFERENCES_EVENT, open)
  }, [])

  const commit = useCallback((categories: ConsentCategories) => {
    writeConsent(categories)
    setDecided(true)
    setDialogOpen(false)
  }, [])

  const acceptAll = useCallback(() => commit({ statistics: true, marketing: true }), [commit])
  const rejectAll = useCallback(() => commit({ statistics: false, marketing: false }), [commit])
  const saveDraft = useCallback(() => commit(draft), [commit, draft])

  const openPreferences = useCallback(() => {
    const current = readConsent()
    setDraft({ statistics: current.statistics, marketing: current.marketing })
    setDialogOpen(true)
  }, [])

  // The SDK embeds the app inside a customer's iframe — no banner there; consent is the host's job.
  if (sdk) return null

  const description = t`At D'Anchiano we use our own- and third-party cookies to remember who you are and your preferences, analyze your behaviour to improve our products and deliver interest-based advertising. You can accept all cookies, reject the non-essential ones, or choose which to allow.`

  return (
    <>
      {!decided && (
        <div
          role="region"
          aria-label={t`Cookie consent`}
          className="cookie-banner bg-background fixed right-0 bottom-0 left-0 z-[1000] flex flex-col gap-4 p-4 shadow-[var(--footer-shadow)] sm:flex-row sm:items-center sm:justify-between print:hidden"
        >
          <div className="flex items-start gap-2.5">
            <span role="img" aria-label="cookie" className="text-2xl leading-none">
              🍪
            </span>
            <p className="text-sm">
              {description}{' '}
              <a href="/cookies-policy" target="_blank" rel="noopener noreferrer" className="underline">
                {t`Cookies policy`}
              </a>
            </p>
          </div>
          <div className="flex flex-col gap-2 sm:shrink-0 sm:flex-row sm:items-center">
            <Button type="button" variant="ghost" onClick={openPreferences}>
              {t`Customize`}
            </Button>
            <Button type="button" variant="outline" onClick={rejectAll}>
              {t`Reject all`}
            </Button>
            <Button type="button" onClick={acceptAll}>
              {t`Accept all`}
            </Button>
          </div>
        </div>
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent aria-describedby={undefined} className="flex max-h-[85vh] flex-col gap-3 overflow-y-auto">
          <h2>{t`Cookie preferences`}</h2>
          <p className="text-muted-foreground text-sm">
            {description}{' '}
            <a
              href="/cookies-policy"
              target="_blank"
              rel="noopener noreferrer"
              className="underline"
              onClick={() => setDialogOpen(false)}
            >
              {t`Cookies policy`}
            </a>
          </p>

          <div className="flex items-center justify-between gap-4">
            <span className="font-medium">{t`Necessary`}</span>
            <Switch
              id="co-necessary"
              checked={true}
              disabled
              onCheckedChange={() => undefined}
              aria-label={t`Necessary cookies are always active`}
            />
          </div>
          <p className="text-muted-foreground text-sm">{t`These cookies are necessary for the website to work and cannot be disabled on our systems.
They are usually set to respond to actions made by you to receive services, such as adjusting your privacy preferences, logging into the site, or filling out forms. You can set your browser to block or alert you about the presence of these cookies, but some parts of the website will not work properly. These cookies do not store any personally identifiable information.`}</p>

          <div className="flex items-center justify-between gap-4">
            <span className="font-medium">{t`Statistics`}</span>
            <Switch
              id="co-statistics"
              checked={draft.statistics}
              onCheckedChange={(value) => setDraft((prev) => ({ ...prev, statistics: value }))}
            />
          </div>
          <p className="text-muted-foreground text-sm">{t`These cookies allow us to count visits and traffic sources in order to measure and improve the performance of our site. They help us know which pages are the most or least popular, and to see how many people visit the site. All information these cookies collect is aggregated and therefore anonymous. If you do not allow these cookies we will not know when you accessed our site, and therefore will not be able to tell when you visited it.`}</p>

          <div className="flex items-center justify-between gap-4">
            <span className="font-medium">{t`Marketing`}</span>
            <Switch
              id="co-marketing"
              checked={draft.marketing}
              onCheckedChange={(value) => setDraft((prev) => ({ ...prev, marketing: value }))}
            />
          </div>
          <p className="text-muted-foreground text-sm">{t`These cookies may be site-wide, placed by our advertising partners. These businesses may use them to create a profile of your interests and show you relevant ads on other sites. They do not store personal information directly, but are based on the unique identification of your browser and Internet access device. If you do not allow these cookies, you will have less targeted advertising.`}</p>

          <div className="mt-2 flex flex-col gap-2 sm:flex-row sm:justify-end">
            <Button type="button" variant="outline" onClick={rejectAll}>
              {t`Reject all`}
            </Button>
            <Button type="button" variant="outline" onClick={saveDraft}>
              {t`Save preferences`}
            </Button>
            <Button type="button" onClick={acceptAll}>
              {t`Accept all`}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  )
}
